import React, {useState} from 'react';
import OeTip from './OeTip';
import DeliverResult from './DeliverResult';
import VeVorschau from './VeVorschau';
import {closeOrder, refreshShortfall} from './api';
import {fmt0, qtyNum} from './format';
import {
    WH_LAHR,
    deliveryStatusMatrix,
    kommScheinDone,
    splitPlan,
    uncoveredPrimaryRows,
} from './wizard';

// Schritt 9 des geführten Ablaufs: Lieferschein ab Lahr und Auftrag
// abschließen. Original `renderWarehouse`, Zweig `WIZ_STEP === 9`
// (4320–4350), `doDelivery` (4479), `doDeliveryStaged` (4403),
// `deliveryStatusMatrix` (4073), `openCloseOrderModal`/`doCloseOrder`
// (4548–4578). ABGLEICH G36–G40, G51–G53, I4, J53, J54, F8.

/** Lieferschein-Chips und Positions-Statusmatrix (Original 4073). */
function StatusMatrix({ctx}) {
    const {rows, stock, order} = ctx;
    const matrix = deliveryStatusMatrix(rows, stock, order);

    if (!matrix.lines.length) {
        return null;
    }

    return (
        <div className="statusmatrix">
            <div className="sm-h">
                📋 Positions-Status
                <OeTip
                    tip={'Zeigt vor der Lieferung, was gedeckt, gesichert oder nicht da ist — und nach der Lieferung, was tatsächlich geliefert wurde.'}
                    detail={'Positionen auf „Keine" werden gar nicht geliefert und zählen in keinem Lieferschein.'}
                >
                    <span className="poinfo muted" style={{display: 'inline', marginLeft: 6}}>?</span>
                </OeTip>
            </div>

            {matrix.shipments.length ? (
                <div className="sm-ls">
                    <span className="muted">Lieferscheine:</span>
                    {matrix.shipments.map((s) => (
                        <OeTip key={s.number} tip={s.open
                            ? `${s.number} ist offen — noch nicht versendet.`
                            : `${s.number} ist abgeschlossen.`}>
                            <span className={`lschip ${s.open ? 'open' : 'done'}`}>{s.number}</span>
                        </OeTip>
                    ))}
                </div>
            ) : null}

            <table className="smtx">
                <thead>
                    <tr>
                        <th>Position</th>
                        <th>Seite</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {matrix.lines.map((l) => (
                        <tr key={l.key}>
                            <td>{l.articleNumber} <span className="muted">{l.title}</span></td>
                            <td><span className={`sm-side ${l.side.cls}`.trim()}>{l.side.label}</span></td>
                            <td><span className={`sm-st ${l.status.cls}`.trim()}>{l.status.label}</span></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function StepLahrLS({
    ctx,
    wizard,
    deliver,
    orderRef,
    dispatch,
    toast,
    ask,
    abilities,
    onStep,
    onDeliver,
    onRefreshDocMeta,
}) {
    const {order, rows, stock} = ctx;
    const plan = splitPlan(WH_LAHR, rows, stock);
    const canShip = !!(abilities && abilities.ship);
    const [fmLaeuft, setFmLaeuft] = useState(false);

    /** „FM-Datei aktualisieren" (Ticket #120): nach einem Storno direkt in weclapp. */
    const fmAktualisieren = async () => {
        setFmLaeuft(true);
        try {
            const j = await refreshShortfall(orderRef);
            if (j && j.skipped) {
                toast(j.error || 'Für diesen Auftrag gibt es noch keinen Lieferlauf aus dem Editor.');
            } else if (j && j.ok) {
                toast(j.shortfalls
                    ? `✓ Fehlmengen-Datei neu berechnet (${j.shortfalls} Position(en))${j.dryRun ? ' — Trockenlauf' : ''}`
                    : '✓ Es fehlt nichts mehr — die Fehlmengen-Datei wurde entfernt.');
            } else {
                toast('Fehlmengen-Datei nicht aktualisiert: ' + ((j && j.error) || 'unbekannt'), {variant: 'danger', autohide: false});
            }
        } catch (e) {
            toast('Fehlmengen-Datei nicht aktualisiert: ' + (e.response?.data?.error || e.message), {variant: 'danger', autohide: false});
        } finally {
            setFmLaeuft(false);
        }
    };

    if (!plan) {
        return null;
    }

    const hasOpen = rows.some(
        (r) => !r.del && r.articleId && (qtyNum(r.quantity) - qtyNum(r.shippedQuantity)) > 0,
    );
    const openShip = !!order.openShipments;
    const blocked = !hasOpen
        ? 'Alle Positionen sind bereits geliefert.'
        : (openShip ? 'Es gibt bereits einen offenen Lieferschein — bitte zuerst abschließen.' : '');

    const stagedFlow = plan.secondaryRows.length > 0;
    const kommDone = kommScheinDone(ctx);
    const verified = !!wizard.kommWiz.verified;
    const uc = uncoveredPrimaryRows(plan, rows, stock);

    const pName = ((stock.warehouses || []).find((w) => String(w.id) === plan.primaryWh) || {}).name || '';
    const sName = (plan.secWh || {}).name || '';
    const cosmosN = plan.cosmosRows.length;
    const cName = ((stock.warehouses || []).find((w) => String(w.id) === '2350209') || {}).name || 'Cosmos';
    const nLS = (plan.secondaryRows.length > 0 ? 2 : 1) + (cosmosN > 0 ? 1 : 0);

    const offenePos = rows.filter(
        (r) => !r.del && r.articleId && (qtyNum(r.quantity) - qtyNum(r.shippedQuantity)) > 0,
    );
    const schonGeschlossen = ['MANUALLY_CLOSED', 'CLOSED', 'CANCELLED'].includes(order.status);

    /** „🏁 Auftrag abschließen" (Original `openCloseOrderModal`, 4548). */
    const abschliessen = () => ask({
        title: 'Auftrag abschließen',
        confirmLabel: 'Auftrag abschließen',
        tone: 'danger',
        body: (
            <>
                <p className="chg">
                    ⚠ Auftrag <b>{order.number}</b> wird abgeschlossen (weclapp-Status „manuell geschlossen").
                </p>
                <p>
                    Bereits gelieferte Positionen bleiben. <b>Der offene Rückstand wird NICHT mehr geliefert</b> und
                    lässt sich danach nur in weclapp wieder öffnen.
                </p>
                {offenePos.length ? (
                    <>
                        <p className="chg"><b>{offenePos.length} Position(en) mit Rückstand</b> (verfallen):</p>
                        <div className="scrolllist">
                            {offenePos.map((r) => (
                                <div key={r.id}>
                                    {r.articleNumber} · {r.title || ''} — offen:{' '}
                                    {fmt0(qtyNum(r.quantity) - qtyNum(r.shippedQuantity))} Stk
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <p className="muted">Kein offener Rückstand — es verfällt nichts.</p>
                )}
            </>
        ),
        onConfirm: async () => {
            toast('Auftrag wird abgeschlossen…');
            try {
                const j = await closeOrder(orderRef);
                if (j.dryRun) {
                    toast(`Trockenlauf: ${j.message || 'Der Auftrag wurde nicht abgeschlossen.'}`);
                    return;
                }
                if (j.ok) {
                    toast(`✓ Auftrag ${j.number || order.number} abgeschlossen (Rückstand nicht geliefert)`);
                    onRefreshDocMeta([]);
                    return;
                }
                toast('Abschluss fehlgeschlagen: ' + (j.error || 'unbekannt'), {variant: 'danger', autohide: false});
            } catch (e) {
                toast('Abschluss fehlgeschlagen: ' + (e.response?.data?.error || e.message),
                    {variant: 'danger', autohide: false});
            }
        },
    });

    let go;
    if (blocked) {
        go = <div className="lieferblock">🚚 <b>{blocked}</b></div>;
    } else if (stagedFlow) {
        go = (
            <div className="liefergo staged">
                <div className={`stagestep${verified ? ' done' : ''}`}>
                    <div className="stagehead">
                        <span className="stagenum">①</span> <b>Kommissionierung geprüft</b>
                    </div>
                    <OeTip tip={verified
                        ? 'Die Kommissionierung ist bereits als geprüft bestätigt.'
                        : (kommDone
                            ? 'Bestätigt, dass die Lahr-Positionen kommissioniert und geprüft sind. Erst danach lässt sich der Lahr-Lieferschein erzeugen. In weclapp ändert sich dadurch nichts.'
                            : 'Erst wenn der Kommissionierschein erzeugt ist (Schritt 4), lässt sich die Kommissionierung bestätigen.')}>
                        <button
                            type="button"
                            className={`chkbtn${verified ? ' on' : ''}`}
                            disabled={!kommDone || verified}
                            onClick={() => dispatch({type: 'wiz-komm', patch: {verified: true}})}
                        >
                            {verified ? '✓ ' : ''}Kommissionierung geprüft — alles vollständig
                        </button>
                    </OeTip>
                    <div className="lg-desc">
                        Bestätigt, dass die Lahr-Positionen kommissioniert und geprüft sind.
                    </div>
                </div>

                <div className="stagestep">
                    <div className="stagehead">
                        <span className="stagenum">②</span> <b>Lahr-Lieferschein</b> · Kundensendung
                    </div>
                    <OeTip tip={canShip
                        ? 'Erzeugt in weclapp den Lieferschein ab Lahr für die Kundensendung, schreibt ihn fest, hängt die Fehlmengen-Datei an den Auftrag und schreibt einen Kommentar. Im Testmodus läuft das als Trockenlauf.'
                        : 'Zum Erzeugen von Lieferscheinen fehlt das Recht „Versand".'}>
                        <button
                            type="button"
                            className="actbtn big"
                            disabled={!verified || openShip || deliver.running || !canShip}
                            onClick={() => onDeliver.run('primary')}
                        >
                            🚚 Lahr-Lieferschein erzeugen
                        </button>
                    </OeTip>
                    <div className="lg-desc">
                        {!verified
                            ? '🔒 Erst „Kommissionierung geprüft" bestätigen.'
                            : (openShip
                                ? <>🔒 Erst den <b>Schneider-Lieferschein in Schritt 3 versenden</b> (dort der Knopf
                                    {' '}„Versenden"), dann greift dieser Schritt.</>
                                : (cosmosN > 0
                                    ? <>Erzeugt den Lieferschein ab Lahr für die Kundensendung und dazu einen
                                        {' '}<b>eigenen Lieferschein ab {cName}</b> ({cosmosN} Pos.).</>
                                    : 'Erzeugt den Lieferschein ab Lahr für die Kundensendung.'))}
                    </div>
                </div>
            </div>
        );
    } else {
        go = (
            <div className="liefergo">
                <OeTip tip={canShip
                    ? 'Erzeugt die Lieferscheine in weclapp, schreibt sie fest, hängt die Fehlmengen-Datei an den Auftrag und schreibt einen Kommentar. Im Testmodus läuft das als Trockenlauf.'
                    : 'Zum Erzeugen von Lieferscheinen fehlt das Recht „Versand".'}>
                    <button
                        type="button"
                        className="actbtn big"
                        disabled={deliver.running || !canShip}
                        onClick={() => onDeliver.run('both')}
                    >
                        🚚 Lieferung erstellen
                    </button>
                </OeTip>
                <div className="lg-desc">
                    Erzeugt <b>{nLS} Lieferschein{nLS > 1 ? 'e' : ''}</b> in weclapp
                    {` (${pName}: ${plan.primaryRows.length} Pos.`
                        + (plan.secondaryRows.length > 0 ? `, ${sName}: ${plan.secondaryRows.length} Pos.` : '')
                        + (cosmosN > 0 ? `, ${cName}: ${cosmosN} Pos.` : '')
                        + ')'}
                    , hängt <b>Fehlmengen als Excel</b> an den Auftrag und schreibt einen <b>Kommentar</b>.
                </div>
            </div>
        );
    }

    return (
        <>
            {uc.length ? (
                <div className="srcnote ucnote" style={{borderColor: 'var(--warn)'}}>
                    <span>
                        ⚠ <b>{uc.length} Lahr-Position(en) ohne Deckung</b> — bitte in Schritt{' '}
                        <OeTip tip={'Springt zum Schritt „Kommissionierung", dort lassen sich die Positionen per Entnahme für diesen Auftrag reservieren.'}>
                            <button type="button" className="link" onClick={() => onStep(6)}>
                                „Kommissionierung" (6)
                            </button>
                        </OeTip>
                        {' '}per Entnahme reservieren. Sonst bleibt der Rest <b>offen und steht in der
                        Fehlmengen-Datei</b>; ein zweiter Lieferschein entsteht nur für Positionen, die
                        ausdrücklich auf Schneider stehen.
                    </span>
                    <ul className="uclist">
                        {uc.map((r) => {
                            const index = rows.indexOf(r);
                            return (
                                <li key={r.id || index}>
                                    <span className="ucart">{r.articleNumber}</span>
                                    <span className="ucname">{r.title}</span>
                                    <span className="ucwahl">
                                        <OeTip tip="Standard: Die fehlende Menge bleibt offen und steht in der Fehlmengen-Datei für den Kunden.">
                                            <span className="ucopt on">als Fehlmenge</span>
                                        </OeTip>
                                        <OeTip
                                            tip="Stellt die Position auf „ab Schneider“: Sie kommt dann mit einem zweiten Lieferschein ab SNV."
                                            detail="Das ist dieselbe Einstellung wie der Regler „Liefern ab“ in der Positionstabelle und wird mit „Speichern“ übernommen."
                                        >
                                            <button
                                                type="button"
                                                className="ucopt"
                                                disabled={index < 0 || !canShip}
                                                onClick={() => dispatch({type: 'set-stamm', index, value: 'SNV'})}
                                            >
                                                ab Schneider als 2. Sendung
                                            </button>
                                        </OeTip>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : null}

            {!blocked ? (
                <VeVorschau
                    ctx={ctx}
                    rows={stagedFlow
                        ? [...plan.primaryRows, ...plan.cosmosRows]
                        : [...plan.primaryRows, ...plan.secondaryRows, ...plan.cosmosRows]}
                />
            ) : null}

            {go}

            {deliver.status ? (
                <div className={`status ${deliver.status.tone}`} style={{marginTop: 8}}>{deliver.status.text}</div>
            ) : null}

            <DeliverResult
                result={deliver.result}
                docs={deliver.docs}
                orderRef={orderRef}
                orderId={order.orderId}
                kommState={wizard.kommState}
                canPrint={!!(abilities && abilities.warehouse)}
                onToast={toast}
            />

            {(order.shipments || []).length ? (
                <div className="fmrefresh">
                    <OeTip
                        tip="Rechnet die Fehlmengen-Datei am Auftrag neu und ersetzt die angehängte Fassung."
                        detail="Nötig, wenn ein Lieferschein direkt in weclapp storniert oder geändert wurde. Ein Storno hier im Editor rechnet die Datei schon von selbst neu. Gerechnet wird mit den Positionen des letzten Lieferlaufs aus dem Editor."
                    >
                        <button
                            type="button"
                            className="actbtn"
                            disabled={fmLaeuft || !canShip}
                            onClick={fmAktualisieren}
                        >
                            {fmLaeuft ? '… rechnet' : '↻ FM-Datei aktualisieren'}
                        </button>
                    </OeTip>
                </div>
            ) : null}

            <StatusMatrix ctx={ctx}/>

            {schonGeschlossen ? (
                <div className="closebox">
                    <div className="cb-desc">✓ Auftrag ist bereits abgeschlossen (<b>{order.status}</b>).</div>
                </div>
            ) : (
                <div className="closebox">
                    <div className="cb-title">🏁 Auftrag abschließen</div>
                    <div className="cb-desc">
                        Beendet den Auftrag. <b>Nicht gelieferte Restmengen (Rückstand) werden danach NICHT mehr
                        geliefert.</b>{' '}
                        {offenePos.length
                            ? <>Aktuell offen: <b>{offenePos.length} Position(en)</b> — die verfallen.</>
                            : 'Aktuell ist alles geliefert — es verfällt nichts.'}
                    </div>
                    <OeTip
                        tip={canShip
                            ? 'Setzt den Auftrag in weclapp auf „manuell geschlossen"; der offene Rückstand verfällt.'
                            : 'Zum Abschließen fehlt das Recht „Versand".'}
                        detail={canShip
                            ? 'Der Rückstand lässt sich danach nur noch in weclapp wieder öffnen. Es kommt vorher eine Rückfrage mit der Liste der verfallenden Positionen; im Testmodus läuft es als Trockenlauf.'
                            : undefined}
                    >
                        <button
                            type="button"
                            className="actbtn"
                            style={{borderColor: 'var(--warn)', color: 'var(--warn)'}}
                            disabled={!canShip}
                            onClick={abschliessen}
                        >
                            🏁 Auftrag abschließen{offenePos.length ? ' (Rückstand verfällt)' : ''}
                        </button>
                    </OeTip>
                </div>
            )}
        </>
    );
}
